import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { navEntries, shopPath } from "@/constants/catalogCategories";
import { COMPANY_ADDRESS, COMPANY_NAME, LEGAL_NAME, SUPPORT_EMAIL } from "@/constants/company";
import Logo from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { useLocale } from "@/context/LocaleContext";
import { api } from "@/lib/api";

export default function Footer() {
  const { user } = useAuth();
  const { country } = useLocale();
  const { data: categoryTree } = useQuery({
    queryKey: ["catalog-categories", country],
    queryFn: async () => (await api.get("/catalog/categories", { params: { country } })).data,
    staleTime: 5 * 60 * 1000,
  });
  const categories = navEntries(categoryTree)
    .filter((entry) => !(entry.dept && entry.category))
    .slice(0, 6);
  return (
    <footer className="border-t border-aura-border bg-aura-bg mt-24">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-5 gap-10">
        <div className="md:col-span-2">
          <div className="mb-3">
            <Logo size={40} wordClassName="text-[28px]" />
          </div>
          <p className="text-sm text-aura-muted max-w-sm leading-relaxed">
            An AI-powered marketplace. Shop, compare, and sell across every category —
            from electronics and home to fashion and more.
          </p>
          <p className="text-sm text-aura-muted max-w-sm leading-relaxed mt-3">
            {COMPANY_NAME} is part of {LEGAL_NAME}.<br />
            {COMPANY_ADDRESS}<br />
            <a href={`mailto:${SUPPORT_EMAIL}`} className="link-underline">{SUPPORT_EMAIL}</a>
          </p>
        </div>
        <div>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-4">Discover</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/shop" className="link-underline">New Arrivals</Link></li>
            {categories.map((c) => (
              <li key={c.key}><Link to={shopPath(c)} className="link-underline">{c.label}</Link></li>
            ))}
            <li><Link to="/feed" className="link-underline">For You</Link></li>
            <li><Link to="/live" className="link-underline">Live Selling</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-4">Sell With Us</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/auth?mode=register&role=seller" className="link-underline">Become a Seller</Link></li>
            <li><Link to="/seller/add" className="link-underline">Closet ingest</Link></li>
            <li><Link to="/seller/photoshoot" className="link-underline">AI Photoshoots</Link></li>
          </ul>
        </div>
        <div>
          <div className="text-[10px] tracking-luxe uppercase text-aura-muted mb-4">Company</div>
          <ul className="space-y-2 text-sm">
            <li><Link to="/features/all-capabilities" className="link-underline">Features</Link></li>
            {user?.role !== "seller" && (
              <li><Link to="/developers" className="link-underline">Graph API</Link></li>
            )}
            <li><Link to="/about" className="link-underline">About</Link></li>
            <li><Link to="/contact" className="link-underline">Contact</Link></li>
            <li><Link to="/privacy" className="link-underline">Privacy Policy</Link></li>
            <li><Link to="/terms" className="link-underline">Terms of Service</Link></li>
            <li><Link to="/acceptable-use" className="link-underline">Acceptable use</Link></li>
            <li><Link to="/returns" className="link-underline">Returns &amp; refunds</Link></li>
            <li><Link to="/cancellations" className="link-underline">Cancellations</Link></li>
          </ul>
        </div>
      </div>
      <div className="border-t border-aura-border py-6 text-center text-xs text-aura-muted">
        © {new Date().getFullYear()} {COMPANY_NAME}. {COMPANY_NAME} is part of {LEGAL_NAME}.
        <span className="mx-2">·</span>
        <a href="https://sovrn.co/1irb5y5" rel="sponsored noopener" target="_blank" className="link-underline">Featured partner offer</a>
      </div>
    </footer>
  );
}
